/* BalanceCard — LED-01, LED-08 */
import { Amount } from '../primitives/Amount';
import { Skeleton } from '../primitives/Skeleton';
import styles from './BalanceCard.module.css';

export interface BalanceCardProps {
  /**
   * "Balance · August", or `LED-08`'s "Balance · as of 23 Nov 2026" when the subscription has lapsed.
   * Written by the caller, because which of those it is depends on the subscription and not on the card.
   */
  readonly eyebrow: string;
  /** The month's movement, in paise. Credit minus debit, and the two below it are what it is made of. */
  readonly balanceMinor: number;
  /** Money in, over the month. Always positive — the direction is which slot it is in. */
  readonly creditMinor: number;
  /** Money out, over the month. Always positive. */
  readonly debitMinor: number;
  /** True while the summary is in flight. Renders the shape at the right size, not a spinner. */
  readonly loading?: boolean;
  /** Shown instead of the figures when the summary could not be fetched. */
  readonly error?: string;
}

/**
 * The first thing on the first screen (`LED-01`).
 *
 * The three figures are shown together because they are one fact: the balance is the difference of the two
 * beneath it, and a person can check the arithmetic without leaving the card. That is the whole reason the
 * screen book puts them in one block rather than three.
 *
 * **The loading state has the same height as the loaded one.** A balance that arrives and pushes the rest
 * of the home screen down is how somebody taps the wrong entry, so the skeleton is the shape of the answer
 * rather than a spinner in a smaller box.
 *
 * **The error state still says which month.** "We could not load August" is a different sentence from "we
 * could not load", and the first one tells somebody whether to try again or to check their filter.
 */
export function BalanceCard({
  eyebrow,
  balanceMinor,
  creditMinor,
  debitMinor,
  loading = false,
  error,
}: BalanceCardProps): React.JSX.Element {
  return (
    <section className={styles.card} aria-label={eyebrow}>
      <p className={styles.eyebrow}>{eyebrow}</p>

      {loading ? (
        <div className={styles.loading} data-testid="balance-card-loading">
          <Skeleton width="9rem" height="2.25rem" />
          <div className={styles.split}>
            <Skeleton width="5rem" height="1.5rem" />
            <Skeleton width="5rem" height="1.5rem" />
          </div>
        </div>
      ) : error ? (
        <p className={styles.error} role="status">
          {error}
        </p>
      ) : (
        <>
          <p className={styles.figure}>
            <Amount amountMinor={balanceMinor} size="xl" />
          </p>
          <div className={styles.split}>
            <div className={styles.half}>
              <span className={styles.label}>Credit</span>
              <Amount amountMinor={creditMinor} tone="credit" size="md" />
            </div>
            <div className={styles.half}>
              <span className={styles.label}>Debit</span>
              <Amount amountMinor={debitMinor} tone="debit" size="md" />
            </div>
          </div>
        </>
      )}
    </section>
  );
}
